// src/app/pages/admin/EventosEmpleabilidad.helpers.tsx

export interface Localidad {
	id_localidad: number;
	desc_localidad: string;
	cod_localidad?: string;
}

export interface ParametroEventoEmpleabilidad {
	id: number;
	type: string;
	code: string;
	value: string;
	label: string;
	active: boolean;
}

export interface EventoEmpleabilidadFranja {
	id?: number;
	franja_horaria_id: number;
	cupo_maximo_prerregistro: number;
	conteo_prerregistrados?: number;
	Franja_horaria?: { id: number; code: string; value: string; label: string };
}

export interface EventoEmpleabilidad {
	id: number;
	codigo_evento: string;
	nombre_evento: string;
	tipo_evento: string;
	dura_mas_de_un_dia: 'SI' | 'NO';
	fecha_inicio_evento: string;
	fecha_fin_evento: string;
	localidad_id: number;
	lugar: string;
	direccion: string | null;
	tipo_poblacion_priorizada: string[] | string | null;
	requiere_prerregistro: 'SI' | 'NO';
	fecha_inicio_prerregistro: string | null;
	fecha_fin_prerregistro: string | null;
	franjas?: EventoEmpleabilidadFranja[] | null;
	/** @deprecated Reemplazado por `franjas`; el backend lo sigue enviando como espejo por compatibilidad. */
	franja_horaria_id?: number | number[] | string | null;
	/** @deprecated Reemplazado por `franjas`; el backend lo sigue enviando como espejo por compatibilidad. */
	cantidad_maxima_prerregistro?: number | null;
	conteo_prerregistrados: number;
	requiere_notificacion_correo: 'SI' | 'NO' | null;
	estado: number | null;
	Localidad?: Localidad;
	Estado?: { id: number; code: string; value: string; label: string };
}

export interface CandidatoBusqueda {
	id: number;
	tipo_documento: string;
	numero_documento: string;
	nombre_completo: string;
}

export const TIPO_EVENTO_POBLACIONAL = 'FP';

// El value del catálogo puede venir con texto adicional (ej. 'FP-Feria Empleo Poblacional'),
// por eso se compara como LIKE 'FP%' y no por igualdad exacta.
export const esEventoPoblacional = (tipoEvento: string | null | undefined): boolean =>
	(tipoEvento ?? '').trim().toUpperCase().startsWith(TIPO_EVENTO_POBLACIONAL);

export const formatDateDisplay = (isoDate: string | null): string => {
	if (!isoDate) return '';
	const [year, month, day] = isoDate.split('T')[0].split('-');
	return `${day}/${month}/${year}`;
};

export const getTodayISO = (): string => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const day = String(now.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export const EMPTY_FORM = {
	codigo_evento: '',
	nombre_evento: '',
	tipo_evento: '',
	tipo_poblacion_priorizada: [] as string[],
	dura_mas_de_un_dia: 'NO',
	fecha_inicio_evento: '',
	fecha_fin_evento: '',
	localidad_id: '',
	lugar: '',
	direccion: '',
	requiere_prerregistro: 'NO',
	fecha_inicio_prerregistro: '',
	fecha_fin_prerregistro: '',
	franjas: [] as { franja_horaria_id: string; cupo_maximo_prerregistro: string; conteo_prerregistrados?: number }[],
	conteo_prerregistrados: 0,
	requiere_notificacion_correo: '',
	estado: '',
};

export type FormState = typeof EMPTY_FORM;

export const buildPayload = (form: FormState, options?: { isCreate?: boolean }) => {
	const requierePrerregistro = form.requiere_prerregistro;
	const payload: any = {
		nombre_evento: form.nombre_evento.trim(),
		tipo_evento: form.tipo_evento,
		dura_mas_de_un_dia: form.dura_mas_de_un_dia,
		fecha_inicio_evento: form.fecha_inicio_evento,
		fecha_fin_evento: form.fecha_fin_evento,
		localidad_id: Number(form.localidad_id),
		lugar: form.lugar.trim(),
		direccion: form.direccion.trim() ? form.direccion.trim() : null,
		tipo_poblacion_priorizada:
			form.tipo_poblacion_priorizada.length > 0 ? form.tipo_poblacion_priorizada : null,
		requiere_prerregistro: requierePrerregistro,
		fecha_inicio_prerregistro: requierePrerregistro === 'SI' ? form.fecha_inicio_prerregistro : null,
		fecha_fin_prerregistro: requierePrerregistro === 'SI' ? form.fecha_fin_prerregistro : null,
		franjas:
			requierePrerregistro === 'SI'
				? form.franjas.map((franja) => ({
						franja_horaria_id: Number(franja.franja_horaria_id),
						cupo_maximo_prerregistro: Number(franja.cupo_maximo_prerregistro),
				  }))
				: null,
		requiere_notificacion_correo:
			requierePrerregistro === 'SI' ? form.requiere_notificacion_correo : null,
		estado: Number(form.estado),
	};

	if (!options?.isCreate) {
		payload.codigo_evento = form.codigo_evento.trim();
	}

	return payload;
};

export const parseTipoPoblacionPriorizada = (value: EventoEmpleabilidad['tipo_poblacion_priorizada']): string[] => {
	if (Array.isArray(value)) return value;
	if (typeof value === 'string' && value.trim()) {
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}
	return [];
};

const parseFranjaHorariaIdLegacy = (value: EventoEmpleabilidad['franja_horaria_id']): number[] => {
	if (Array.isArray(value)) return value.map(Number);
	if (typeof value === 'number') return [value];
	if (typeof value === 'string' && value.trim()) {
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed.map(Number) : [Number(parsed)];
		} catch {
			return [];
		}
	}
	return [];
};

// Eventos creados antes de la tabla eventos_empleabilidad_franjas solo tienen las franjas en
// las columnas heredadas: se reconstruyen desde ahí para no enviar `franjas` vacío al actualizar.
const getFranjasEvento = (evento: EventoEmpleabilidad): FormState['franjas'] => {
	if (Array.isArray(evento.franjas) && evento.franjas.length > 0) {
		return evento.franjas.map((franja) => ({
			franja_horaria_id: String(franja.franja_horaria_id),
			cupo_maximo_prerregistro: String(franja.cupo_maximo_prerregistro),
			conteo_prerregistrados: Number(franja.conteo_prerregistrados ?? 0),
		}));
	}
	if (evento.requiere_prerregistro !== 'SI' || !evento.cantidad_maxima_prerregistro) return [];
	return parseFranjaHorariaIdLegacy(evento.franja_horaria_id)
		.filter((id) => !Number.isNaN(id))
		.map((id) => ({
			franja_horaria_id: String(id),
			cupo_maximo_prerregistro: String(evento.cantidad_maxima_prerregistro),
		}));
};

// Cupo total del evento = suma de los cupos máximos de cada franja (mismo origen que el formulario).
export const getCupoTotalPrerregistro = (evento: EventoEmpleabilidad): number | null => {
	const franjas = getFranjasEvento(evento);
	if (franjas.length === 0) return null;
	return franjas.reduce((total, franja) => total + (Number(franja.cupo_maximo_prerregistro) || 0), 0);
};

export const eventoToForm =(evento: EventoEmpleabilidad): FormState => ({
	codigo_evento: evento.codigo_evento || '',
	nombre_evento: evento.nombre_evento || '',
	tipo_evento: evento.tipo_evento || '',
	tipo_poblacion_priorizada: parseTipoPoblacionPriorizada(evento.tipo_poblacion_priorizada),
	dura_mas_de_un_dia: evento.dura_mas_de_un_dia || 'NO',
	fecha_inicio_evento: evento.fecha_inicio_evento || '',
	fecha_fin_evento: evento.fecha_fin_evento || '',
	localidad_id: evento.localidad_id ? String(evento.localidad_id) : '',
	lugar: evento.lugar || '',
	direccion: evento.direccion || '',
	requiere_prerregistro: evento.requiere_prerregistro || 'NO',
	fecha_inicio_prerregistro: evento.fecha_inicio_prerregistro || '',
	fecha_fin_prerregistro: evento.fecha_fin_prerregistro || '',
	franjas: getFranjasEvento(evento),
	conteo_prerregistrados: evento.conteo_prerregistrados ?? 0,
	requiere_notificacion_correo: evento.requiere_notificacion_correo || '',
	estado: evento.estado ? String(evento.estado) : '',
});

export const getErrorMessage = (error: any): string => {
	if (error?.response?.data?.error) return error.response.data.error;
	return 'Ocurrió un error procesando la solicitud.';
};
